import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Bot, Play, RefreshCw, Settings, CheckCircle2, AlertTriangle,
  Mail, Send, FileEdit, Trash2, Eye, X, Shield, Plus,
} from "lucide-react";

const CATEGORY_COLORS = {
  info: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  tarifs: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  horaires: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  faq: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  inscription: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  urgent: "bg-red-500/20 text-red-300 border-red-500/40",
  plainte: "bg-orange-500/20 text-orange-300 border-orange-500/40",
  sponsor: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  media: "bg-purple-500/20 text-purple-300 border-purple-500/40",
  vip: "bg-yellow-500/25 text-yellow-200 border-yellow-500/50",
  personnel: "bg-pink-500/15 text-pink-300 border-pink-500/30",
  spam: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
  autre: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
};

const ACTION_LABEL = {
  auto_sent: { label: "Envoyé auto", icon: Send, color: "text-emerald-400" },
  drafted: { label: "Brouillon créé", icon: FileEdit, color: "text-blue-300" },
  ignored: { label: "Ignoré", icon: X, color: "text-zinc-500" },
  skipped_spam: { label: "Spam ignoré", icon: X, color: "text-zinc-500" },
  skipped_vip: { label: "VIP — protégé", icon: Shield, color: "text-yellow-300" },
  error: { label: "Erreur", icon: AlertTriangle, color: "text-red-400" },
};

export default function CamilleEmailModule() {
  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [running, setRunning] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [showPreview, setShowPreview] = useState(null);
  const [newVip, setNewVip] = useState("");

  const load = useCallback(async () => {
    try {
      const [c, l, s] = await Promise.all([
        api.get("/camille/config"),
        api.get("/camille/logs?limit=50"),
        api.get("/camille/stats"),
      ]);
      setConfig(c.data);
      setLogs(l.data);
      setStats(s.data);
    } catch {
      toast.error("Erreur chargement Camille");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runCamille = async (dryRun) => {
    setRunning(true);
    try {
      const { data } = await api.post(`/camille/run?dry_run=${dryRun}`);
      toast.success(`${data.processed} courriels traités · ${dryRun ? "preview" : "appliqué"}`);
      if (dryRun) {
        setShowPreview(data.results);
      } else {
        await load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur Camille");
    } finally { setRunning(false); }
  };

  const saveConfig = async (patch) => {
    try {
      const { data } = await api.put("/camille/config", patch);
      setConfig(data);
      toast.success("Configuration sauvegardée");
    } catch { toast.error("Échec sauvegarde"); }
  };

  const addVip = () => {
    const e = newVip.trim().toLowerCase();
    if (!e || !e.includes("@")) return toast.error("Email invalide");
    if ((config.vip_emails || []).includes(e)) return toast.error("Déjà dans la liste VIP");
    saveConfig({ vip_emails: [...(config.vip_emails || []), e] });
    setNewVip("");
  };

  const removeVip = (e) => {
    saveConfig({ vip_emails: (config.vip_emails || []).filter(x => x !== e) });
  };

  if (!config) return <div className="p-8 text-zinc-400">Chargement de Camille...</div>;

  const total = stats?.total || 0;
  const autoSent = stats?.by_action?.auto_sent || 0;
  const drafted = stats?.by_action?.drafted || 0;
  const skippedVip = stats?.by_action?.skipped_vip || 0;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="camille-email-module">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-pink-500 flex items-center justify-center">
              <Bot className="w-7 h-7 text-white" />
            </div>
            Camille
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Réceptionniste IA · Réponses automatiques + Brouillons intelligents · {total} courriels traités
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="camille-preview-btn"
            disabled={running}
            onClick={() => runCamille(true)}
            className="px-4 py-2 rounded-lg border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-zinc-100 text-sm flex items-center gap-2 disabled:opacity-50"
          >
            <Eye className="w-4 h-4" /> Aperçu (sans envoi)
          </button>
          <button
            data-testid="camille-run-btn"
            disabled={running}
            onClick={() => runCamille(false)}
            className="px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-pink-500 text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-50 hover:from-amber-400 hover:to-pink-400"
          >
            {running ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Lancer Camille
          </button>
          <button
            data-testid="camille-config-btn"
            onClick={() => setShowConfig(true)}
            className="p-2 rounded-lg border border-zinc-700 bg-zinc-900 hover:bg-zinc-800"
          >
            <Settings className="w-4 h-4 text-zinc-300" />
          </button>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard label="Auto envoyés" value={autoSent} color="text-emerald-300" icon={Send} />
        <StatCard label="Brouillons" value={drafted} color="text-blue-300" icon={FileEdit} />
        <StatCard label="VIP protégés" value={skippedVip} color="text-yellow-300" icon={Shield} />
        <StatCard label="Total traités" value={total} color="text-white" icon={Mail} />
      </div>

      {/* STATUS BANNER */}
      <div className={`mb-6 p-4 rounded-lg border ${config.enabled ? "border-emerald-500/30 bg-emerald-500/5" : "border-zinc-700 bg-zinc-900/50"}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${config.enabled ? "bg-emerald-400 animate-pulse" : "bg-zinc-500"}`} />
            <div>
              <div className="text-sm font-semibold text-white">
                {config.enabled ? "Camille active" : "Camille en pause"}
              </div>
              <div className="text-xs text-zinc-400 mt-0.5">
                Mode : {config.auto_send_safe_categories ? "Auto-envoi (catégories sûres) + brouillons (sensible)" : "Brouillons uniquement"}
              </div>
            </div>
          </div>
          <button
            data-testid="camille-toggle-enabled"
            onClick={() => saveConfig({ enabled: !config.enabled })}
            className={`px-4 py-1.5 rounded-lg text-xs font-semibold ${config.enabled ? "bg-zinc-800 text-zinc-300 hover:bg-zinc-700" : "bg-emerald-500 text-white hover:bg-emerald-400"}`}
          >
            {config.enabled ? "Mettre en pause" : "Activer"}
          </button>
        </div>
      </div>

      {/* LOGS TABLE */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-white">Historique des courriels traités</h2>
          <button onClick={load} className="text-xs text-zinc-400 hover:text-white flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Actualiser
          </button>
        </div>
        {logs.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm">
            Aucun courriel traité pour l'instant. Clique <strong>Aperçu</strong> pour voir ce que Camille ferait.
          </div>
        ) : (
          <div className="divide-y divide-zinc-800">
            {logs.map((log) => {
              const A = ACTION_LABEL[log.action] || ACTION_LABEL.ignored;
              const Icon = A.icon;
              const cat = log.classification?.category || "autre";
              return (
                <div key={log.id} className="p-4 hover:bg-zinc-900/40 transition" data-testid={`camille-log-${log.id}`}>
                  <div className="flex items-start gap-3">
                    <div className={`mt-1 ${A.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded border ${CATEGORY_COLORS[cat] || CATEGORY_COLORS.autre}`}>
                          {cat}
                        </span>
                        <span className={`text-xs font-medium ${A.color}`}>{A.label}</span>
                        {log.classification?.urgency === "critical" && (
                          <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/40">CRITIQUE</span>
                        )}
                        <span className="text-xs text-zinc-500 ml-auto">
                          {new Date(log.created_at).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" })}
                        </span>
                      </div>
                      <div className="text-sm text-white truncate">{log.subject}</div>
                      <div className="text-xs text-zinc-400 truncate mt-0.5">
                        De : <span className="text-zinc-300">{log.from_email}</span>
                      </div>
                      {log.response_body && (
                        <details className="mt-2">
                          <summary className="text-xs text-amber-300 cursor-pointer hover:text-amber-200">
                            Voir la réponse de Camille
                          </summary>
                          <pre className="mt-2 p-3 bg-zinc-900 rounded text-xs text-zinc-300 whitespace-pre-wrap font-sans">
                            {log.response_body}
                          </pre>
                        </details>
                      )}
                      {log.error && (
                        <div className="text-xs text-red-400 mt-1">Erreur : {log.error}</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* PREVIEW MODAL */}
      {showPreview && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm p-4 flex items-center justify-center" onClick={() => setShowPreview(null)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-3xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900">
              <h3 className="font-semibold text-white">Aperçu Camille — {showPreview.length} courriels</h3>
              <button onClick={() => setShowPreview(null)} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3">
              {showPreview.map((r, i) => (
                <div key={i} className="p-3 rounded border border-zinc-800 bg-zinc-950">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded border ${CATEGORY_COLORS[r.category] || CATEGORY_COLORS.autre}`}>{r.category}</span>
                    {r.is_vip && <span className="text-xs px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-300 border border-yellow-500/40">VIP</span>}
                    <span className="text-xs text-zinc-400">→ {r.would_action || r.action}</span>
                  </div>
                  <div className="text-xs text-zinc-300">De : {r.from}</div>
                  {r.preview && (
                    <pre className="mt-2 text-xs text-zinc-300 whitespace-pre-wrap font-sans line-clamp-6">{r.preview}</pre>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CONFIG MODAL */}
      {showConfig && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm p-4 flex items-center justify-center" onClick={() => setShowConfig(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900">
              <h3 className="font-semibold text-white">Configuration Camille</h3>
              <button onClick={() => setShowConfig(false)} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-5">
              <div>
                <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Mode autonomie</label>
                <div className="flex gap-2">
                  <button
                    data-testid="camille-mode-auto"
                    onClick={() => saveConfig({ auto_send_safe_categories: true })}
                    className={`flex-1 px-3 py-2 rounded text-xs ${config.auto_send_safe_categories ? "bg-emerald-500/20 border border-emerald-500/40 text-emerald-200" : "border border-zinc-700 text-zinc-400"}`}
                  >Auto-envoi + brouillons sensibles</button>
                  <button
                    data-testid="camille-mode-draft"
                    onClick={() => saveConfig({ auto_send_safe_categories: false })}
                    className={`flex-1 px-3 py-2 rounded text-xs ${!config.auto_send_safe_categories ? "bg-blue-500/20 border border-blue-500/40 text-blue-200" : "border border-zinc-700 text-zinc-400"}`}
                  >Brouillons uniquement</button>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Filtre Gmail (requête)</label>
                <input
                  data-testid="camille-polling-input"
                  type="text"
                  value={config.polling_query || ""}
                  onChange={(e) => setConfig({ ...config, polling_query: e.target.value })}
                  onBlur={() => saveConfig({ polling_query: config.polling_query })}
                  className="w-full px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-white"
                />
                <div className="text-xs text-zinc-500 mt-1">Ex : <code>in:inbox is:unread newer_than:7d</code></div>
              </div>

              <div>
                <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Max courriels par exécution</label>
                <input
                  data-testid="camille-max-input"
                  type="number"
                  min="1" max="50"
                  value={config.max_per_run || 10}
                  onChange={(e) => setConfig({ ...config, max_per_run: parseInt(e.target.value) || 10 })}
                  onBlur={() => saveConfig({ max_per_run: config.max_per_run })}
                  className="w-32 px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-white"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-zinc-300 mb-1.5 block flex items-center gap-2">
                  <Shield className="w-3.5 h-3.5 text-yellow-400" />
                  Liste VIP — JAMAIS d'auto-réponse à ces adresses
                </label>
                <div className="space-y-1.5 mb-2">
                  {(config.vip_emails || []).map((e) => (
                    <div key={e} className="flex items-center justify-between px-3 py-1.5 rounded bg-yellow-500/5 border border-yellow-500/20 text-xs">
                      <span className="text-yellow-200">{e}</span>
                      <button data-testid={`camille-vip-remove-${e}`} onClick={() => removeVip(e)} className="text-zinc-500 hover:text-red-400">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    data-testid="camille-vip-input"
                    type="email"
                    placeholder="nouvelle.adresse@exemple.com"
                    value={newVip}
                    onChange={(e) => setNewVip(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addVip()}
                    className="flex-1 px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-white"
                  />
                  <button data-testid="camille-vip-add-btn" onClick={addVip} className="px-3 py-2 rounded bg-yellow-500/20 border border-yellow-500/40 text-yellow-200 text-xs flex items-center gap-1">
                    <Plus className="w-3.5 h-3.5" /> Ajouter
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div className="p-4 rounded-lg border border-zinc-800 bg-zinc-950/50">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-zinc-500">{label}</div>
          <div className={`text-2xl font-bold ${color} mt-1`}>{value}</div>
        </div>
        <Icon className="w-5 h-5 text-zinc-600" />
      </div>
    </div>
  );
}
